import type { LucideIconName } from '@highereducation/hen-ui';

const SOCIAL_ICONS: Record<string, LucideIconName> = {
  linkedin: 'linkedin',
  twitter: 'twitter',
  facebook: 'facebook',
  instagram: 'instagram',
  youtube: 'youtube',
  pinterest: 'pin',
  tiktok: 'music',
  snapchat: 'ghost',
  threads: 'at-sign',
  website: 'globe',
  email: 'mail',
};

/**
 * Maps normalized social keys to the Cloudinary-hosted brand icon names
 * used in the Supersonic "social Icons" folder.
 */
const BRAND_ICON_NAMES: Record<string, { icon: string; type: string }> = {
  linkedin: { icon: 'linkedin', type: 'social' },
  twitter: { icon: 'twitter', type: 'social' },
  facebook: { icon: 'facebook', type: 'social' },
  instagram: { icon: 'instagram', type: 'social' },
  youtube: { icon: 'youtube', type: 'social' },
  website: { icon: 'globe-alt', type: 'solid' },
  email: { icon: 'mail', type: 'solid' },
};

/** Normalize WP repeater keys (`contributors_linkedin` → `linkedin`). */
export function normalizedContributorSocialKey(key: string): string {
  return key.toLowerCase().replace(/^contributors_/, '');
}

export function contributorSocialIcon(key: string): LucideIconName {
  return SOCIAL_ICONS[normalizedContributorSocialKey(key)] ?? 'link';
}

/** Cloudinary URL for the filled brand-style icon matching the old site/app. */
export function contributorSocialBrandIconUrl(
  key: string,
  color = '1C1E21',
  size = 20,
): string | undefined {
  const normalized = normalizedContributorSocialKey(key);
  const entry = BRAND_ICON_NAMES[normalized];
  if (!entry) return undefined;
  const transforms = [
    'f_png',
    'c_fill',
    'q_auto',
    'dpr_2.0',
    'e_colorize',
    `co_rgb:${color}`,
    `h_${size}`,
    `w_${size}`,
  ].join(',');
  return `https://res.cloudinary.com/highereducation/image/upload/${transforms}/v1/Supersonic/${encodeURIComponent(
    entry.type + ' Icons',
  )}/${entry.icon}-${entry.type}.svg`;
}

export function contributorSocialHref(key: string, value: string): string {
  const trimmedValue = value.trim();
  if (!trimmedValue) return trimmedValue;
  const normalizedKey = normalizedContributorSocialKey(key);
  if (
    normalizedKey === 'email' &&
    !/^mailto:/i.test(trimmedValue) &&
    !/^https?:/i.test(trimmedValue)
  ) {
    return `mailto:${trimmedValue}`;
  }
  return trimmedValue;
}

export function isContributorEmailSocialKey(key: string): boolean {
  return normalizedContributorSocialKey(key) === 'email';
}
